import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { RandomGeneratorService } from '../helper/random-generator.service';
import { isNullOrUndefined } from '../helper/sla-util.helper';

/**
 * Der SiteService laedt eine Konfigurationsdatei "site-config.json" von dem Server
 * und ermoeglicht es, Angular Anwendungen zu konfigurieren. Die entsprechende Konfigurationsdatei
 * wird immer aus dem '/' Verzeichnis geladen und ist somit fuer mehrere Anwendungen gleichzeitig gedacht.
 *
 * Beispiel einer Konfigurationsdatei:
 * @example
 *  {
 *     "https://demo.sla.de:50000/app/tumbler-manager/": {
 *         "websocket": "wss://192.168.178.50:8080/sla-tumbler-manager/ws"
 *     },
 *     "http://demo.sla.de:50000//app/wms/": {
 *         "rest": "http://192.168.178.51/50000/sla-warehouse-rest/rest/"
 *     }
 *   }
 *
 * Die ID der Konfigurationsparameter stammt aus der Anwendungs-URI, d.h. eine Anwendung die sich im Unterverzeichnis
 * '/app/wms' befindet, laedt die Parameter aus dem JSON-Teil '/app/wms'.
 *
 * Mit der Methode getConfigKey(key: string), kann ein Parameter aus der Datei ausgelesen werden. Derzeit werden die Parameter
 * fuer die Konfiguration der Websockets, der REST-Schnittstelle oder beispielsweise der SSC-Schnittstelle benutzt.
 *
 * Damit der Service die Datei vor jeglicher Logik-Ausfuehrung laedt, muss diese in der app.module.ts wie folgt definiert werden:
 * @example
 * ...
 *
 * export function initConfig(appConfig: SiteService) {
 *   return () => appConfig.load();
 * }
 *
 * ...
 *
 * @NgModule({
 *     imports: [
 *       ...
 *     ],
 *     declarations: [
 *       ...
 *     ],
 *     providers: [
 *
 *       SiteService,
 *       {provide: APP_INITIALIZER, useFactory: initConfig, deps: [SiteService], multi: true},
 *       ...
 *     ],
 *     bootstrap: [AppComponent]
 * })
 */
@Injectable({
    providedIn: 'root',
})
export class SiteService {

    private _config: any = null;
    private _configSub: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    constructor(protected myHttpClient: HttpClient,
                protected myRandomGeneratorService: RandomGeneratorService,
    ) {
    }

    /**
     * Liefert ein Observable welcher sich meldet sobald die Config geladen wurde
     */
    public subConfig(): Observable<boolean> {
        return this._configSub.asObservable();
    }

    /**
     * Liefert ein Objekt der Angular-Konfigurationsdatei
     */
    public getConfigKey(key: string): any {
        return this._config[ key ];
    }

    /**
     * Manuelles setzen eines Wertes der Angular-Konfigurationsdatei
     */
    public setConfigKey(key: string, value: any): void {
        this._config[ key ] = value;
    }

    /**
     * Diese Methode lädt die Konfigurationsdatei des Tomcats
     * @param path
     */
    public load(path?: string): Promise<any> {

        return new Promise((resolve: any): void => {
            const pathTmp: string = path || `/site-config.json?v=${this.myRandomGeneratorService.generateAlphaNumberId(10)}`;

            this.myHttpClient.get(pathTmp).subscribe((config: any) => {
                if (isNullOrUndefined(config)) {
                    if (path) {
                        console.error('Server configuration file could not be read');
                        this.heuristicGuess();
                        resolve(true);
                    } else {
                        void this.load(`assets/cordova-config.json?v=${this.myRandomGeneratorService.generateAlphaNumberId(10)}`).then((result: any) => {
                            resolve(result);
                        });
                    }
                } else {
                    if (path) {
                        this.parseServerConfigurationFile(config, true);
                    } else {
                        this.parseServerConfigurationFile(config);
                    }
                    resolve(true);
                }
            }, (_err: unknown) => {
                if (path) {
                    console.error('Server configuration file could not be read');
                    this.heuristicGuess();
                    resolve(true);
                } else {
                    void this.load(`assets/cordova-config.json?v=${this.myRandomGeneratorService.generateAlphaNumberId(10)}`).then((result: any) => {
                        resolve(result);
                    });
                }
            });

        });
    }

    private parseServerConfigurationFile(serverConfig: Record<string, unknown>, cordova?: boolean): void {
        let pathname: string = window.location.origin + window.location.pathname;
        if (cordova) {
            pathname = 'cordova';
        }
        let config: unknown = serverConfig[ pathname ] || serverConfig[ `${pathname}index.html` ] || serverConfig[ pathname.replace('index.html', '') ];

        /* Iterate and check if pathname starts with one of the config-keys
         this is needed to find configs for applications that do not use an anchor-element
         */
        if (!config) {
            for (const configElement in serverConfig) {
                if (pathname.startsWith(configElement)) {
                    console.log(`Found config-key by substring: ${configElement}`);
                    config = serverConfig[ configElement ];
                    break;
                }
            }
        }

        // If config is still not found, guess
        if (!config) {
            this.heuristicGuess();
        } else {
            this._config = config;
            this._configSub.next(true);
        }
    }

    private heuristicGuess(): void {
        let wsProtocol: string;
        if (window.location.protocol === 'http:') {
            wsProtocol = 'ws:';
        } else if (window.location.protocol === 'https:') {
            wsProtocol = 'wss:';
        } else {
            return; // ??
        }
        let portString: string = `:${window.location.port}`;
        if (portString === ':') {
            portString = '';
        }
        const webPath: string = window.location.pathname.replace(new RegExp('^(.*)-(web|ionic)/.*', 'g'), '$1-rest/');
        const restPath: string = window.location.pathname.replace(new RegExp('^(.*)-(web|ionic)/.*', 'g'), '$1-rest/api/1/');
        const wsPath: string = window.location.pathname.replace(new RegExp('^(.*)-(web|ionic)/.*', 'g'), '$1-rest/ws');
        const websockifyPath: string = `${window.location.pathname.split('-')[ 0 ]}-websockify-rest/api/1/`;

        this._config = {
            web: `${window.location.protocol}//${window.location.hostname}${portString}${webPath}`,
            rest: `${window.location.protocol}//${window.location.hostname}${portString}${restPath}`,
            websocket: `${wsProtocol}//${window.location.hostname}${portString}${wsPath}`,
            websockify: `${window.location.protocol}//${window.location.hostname}${portString}${websockifyPath}`,
        };
        this._configSub.next(true);
        console.log(`guessing web: ${this._config.web} rest: ${this._config.rest} websocket: ${this._config.websocket}`);
    }
}
